import { format } from 'date-fns';
import { type FC, useState } from 'react';
import { Badge, Box, Button, Column, Expanded, IconButton, Row, Typography } from '@tempmee/react';
import { Tooltip } from '~/commonComponents/TooltipWrapper/TooltipWrapper';
import isHoliday from '~/components/Request/isHoliday';
import { isDailyOvertime } from '~/components/Request/multiday/utils';
import { FlipCard } from '~/components/shared/FlipCard';
import { useAverageRate } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import BulbIcon from '~/images/icons/BulbIcon';
import { OutlinePencilIcon, OutlineTrashIcon, SolidExclamationWarningAlertIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Colors } from '~/themes/colors';
import BackTipsCard from './BackTipsCard';

// no DS props for boxShadow, backgroundColor, borderRadius
const CARD_SURFACE_STYLE = {
  backgroundColor: colors.shades[0],
  borderRadius: 8,
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 3px 10px 1px rgba(0, 0, 0, 0.15)',
} as const;

// no DS prop for borderBottom
const HEADER_BORDER_STYLE = { borderBottom: `1px solid ${Colors.neutral_100}` } as const;

// TEMP-8308: tips show for shifts starting within 36h whose rate is at most 105% of the office's average.
const TIPS_WINDOW_HOURS = 36;
const TIPS_MAX_PERCENT_OF_AVERAGE = 105;

interface IShiftReviewCardProps {
  date: Date;
  officeId: string;
  professionType: string;
  specialty: string;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
  rate: number;
  isAssistedHygiene?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const parseTimeToDate = (date: Date, time: string): Date => {
  const result = new Date(date);
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);

  if (!match) return result;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toLowerCase();

  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;

  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const ShiftReviewCard: FC<IShiftReviewCardProps> = ({
  date,
  officeId,
  professionType,
  specialty,
  arrivalTime,
  departureTime,
  mealBreakDuration,
  isMealBreakPaid,
  rate,
  isAssistedHygiene,
  onEdit,
  onDelete,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const { data: averageRating } = useAverageRate(officeId);
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);

  const professionCode = UserProfession.getCode(professionType) as string | undefined;
  const averageRate = professionCode ? averageRating?.[professionCode]?.average : undefined;

  const workerClassificationType = selectedChildOffice?.workerClassification?.classificationType;
  const overtimeLimits = workerClassificationType
    ? selectedChildOffice?.overtimeLimits?.[workerClassificationType]
    : undefined;

  const jobForOvertime = {
    localStart: arrivalTime,
    localEnd: departureTime,
    break_time: mealBreakDuration,
    is_break_paid: isMealBreakPaid,
  };

  const isOvertime = isDailyOvertime(jobForOvertime, overtimeLimits?.daily);

  const now = new Date();
  const shiftStart = parseTimeToDate(date, arrivalTime);
  const hoursUntilShift = (shiftStart.getTime() - now.getTime()) / (1000 * 60 * 60);
  // No tips without the office's real average: there is nothing to compare the rate against.
  const isAtOrBelowAverage = averageRate
    ? Math.round((rate / averageRate) * 100) <= TIPS_MAX_PERCENT_OF_AVERAGE
    : false;
  const showTips =
    hoursUntilShift <= TIPS_WINDOW_HOURS && hoursUntilShift > 0 && isAtOrBelowAverage;

  const formattedDate = format(date, 'EEE, MMM dd, yyyy');
  const holidayDate = new Date(date);
  holidayDate.setHours(8, 0, 0, 0);
  const isHolidayDate = isHoliday(holidayDate);

  const specialtyDisplay = specialty?.split('_')?.[1] || 'General Dentistry';

  const handleTipsClick = () => {
    setIsFlipped((prev) => !prev);
  };

  // border is dynamic based on showTips — cannot be part of CARD_SURFACE_STYLE
  const cardBorder = showTips ? `2px solid ${colors.secondary[500]}` : '2px solid transparent';

  const cardFront = (
    <Column width="100%" height="100%" style={{ ...CARD_SURFACE_STYLE, border: cardBorder }}>
      {/* Header */}
      <Row justify="between" align="center" pl={4} pr={4} pt={4} pb={4} style={HEADER_BORDER_STYLE}>
        <Row align="center" gap={2}>
          <Typography
            as="span"
            size="heading_4"
            bold
            color={colors.neutral[600]}
            data-testid="review-date"
            data-cy="review-date"
          >
            {formattedDate}
          </Typography>
          {isHolidayDate && (
            <Badge
              severity="secondary"
              data-testid="holiday-badge"
              data-cy="holiday-badge"
            >
              HOLIDAY
            </Badge>
          )}
        </Row>
        <Row gap={2}>
          <IconButton
            type="button"
            appearance="default"
            color="secondary"
            size="small"
            data-testid="edit-shift"
            data-cy="edit-shift"
            onClick={onEdit}
            aria-label="Edit shift"
            Icon={<OutlinePencilIcon color={colors.secondary[500]} />}
          />
          <IconButton
            type="button"
            appearance="default"
            color="secondary"
            size="small"
            data-testid="remove-shift"
            data-cy="remove-shift"
            onClick={onDelete}
            aria-label="Remove shift"
            Icon={<OutlineTrashIcon color={colors.secondary[500]} />}
          />
        </Row>
      </Row>

      {/* Body — grows to fill available height */}
      <Expanded>
        <Column pl={6} pr={4} pt={2} gap={1}>
          <Typography as="span" variant="body1" color={colors.neutral[600]}>
            {professionType}
          </Typography>
          <Typography as="span" variant="body1" color={colors.neutral[600]}>
            {specialtyDisplay}
          </Typography>
          <Row align="center" gap={1}>
            <Typography as="span" variant="body1" color={colors.neutral[600]}>
              {arrivalTime} - {departureTime}
            </Typography>
            {isOvertime && (
              <Tooltip text="Overtime">
                <Box>
                  <SolidExclamationWarningAlertIcon color={colors.warning[500]} />
                </Box>
              </Tooltip>
            )}
            <Typography as="span" variant="body1" color={colors.neutral[600]}>
              / Meal Break: {mealBreakDuration} {isMealBreakPaid ? '(Paid)' : '(Unpaid)'}
            </Typography>
          </Row>
          {isAssistedHygiene !== undefined && (
            <Typography as="span" variant="body1" color={colors.neutral[600]}>
              Assisted Hygiene Available: {isAssistedHygiene ? 'Yes' : 'No'}
            </Typography>
          )}
          <Row align="center" gap={1}>
            {showTips && <BulbIcon color={colors.secondary[500]} />}
            <Typography
              as="span"
              variant="body1"
              color={colors.neutral[600]}
              data-testid="review-rate"
              data-cy="review-rate"
            >
              {`$${rate}/hr`}
            </Typography>
          </Row>
        </Column>
      </Expanded>

      {/* Footer — Tips button right-aligned; only shown when showTips */}
      {showTips && (
        <Row justify="end" pr={4} pb={4}>
          <Button
            type="button"
            variant="outlined"
            size="xs"
            data-testid="tips-button"
            data-cy="tips-button"
            onClick={handleTipsClick}
            aria-label="View tips"
            LeadingIcon={<BulbIcon />}
          >
            Tips
          </Button>
        </Row>
      )}
    </Column>
  );

  return (
    <Box data-testid="shift-review-card" data-cy="shift-review-card">
      <FlipCard
        isFlipped={isFlipped}
        front={cardFront}
        back={<BackTipsCard onEdit={onEdit} onReturnClick={handleTipsClick} />}
        width={438}
        height={256}
      />
    </Box>
  );
};
